//* react
import React from 'react';

//* material-ui
import Grid from '@mui/material/Grid2';
import { Box, Link } from '@mui/material';

//* types
import { ChatUserModel } from 'types/chat-users/chat-user';
import {
  ConversationOpenStatusSocketType,
  LabelUpdateSocketType,
  ReceiveMessageSocketType,
  ReceiveTemplateMessageSocketType,
  SocketEventType,
  UpdateChatUserDontDisturbSocketType,
  UpdateChatUserUnreadSocketType,
  UpdateCompanyPlatformsSocketType,
  UpdateMessageSocketType,
} from 'types/socket';
import { MessageModel } from 'types/chat/messages/message';

//* system
import { dispatch, store } from 'store';
import { doesUserMatchCurrentFilters, getMessageStatus } from 'utils/chat';
import {
  saveSingleMessageToUser,
  setChatUsers,
  setSelectedChatUser,
  updateSingleChatUser,
  updateSingleChatUserByPhone,
} from 'store/slices/chat';
import { writePerformanceLog } from 'utils/logs';
import { getBrowserNotifStatus, handleBrowserNotification, safeParseJSON } from 'utils/helpers';
// import { enqueueSnackbar } from 'notistack';
import { useNavigate } from 'react-router';
import useAuth from './useAuth';
import { updateSingleApplicationUser } from 'store/slices/applicationUsers';
import { CompanyPlatformsModel } from 'types/company-platforms/companyPlatforms';
import { updateCompanyPlatformsData } from 'store/slices/companyPlatforms';
import { useSocketListeners } from './useSocketListeners';
import { useSnackbar } from 'notistack';
import NotificationIcon from 'ui-component/third-party/NotificationIcon';
import { ChatViewUsersId, getCurrentView } from './useCurrentChatView';
import {
  addUnreadUser,
  processUnreadUser,
  removeUnreadUser,
  updateUnreadUserByPhone,
} from 'store/slices/unread-chat-users';
import { getChatUser } from 'api/chat-users';

/**
 * Hook for handling global socket events
 */

const socketEvents: Array<SocketEventType> = [
  'ReceiveMessage',
  'message:new',
  'MessageStatusUpdate',
  'ReceiveTemplateMessage',
  'unreadNotification',
  'UnreadUpdate',
  'updateChatUserUnread',
  'Notification',
  'TaggedUsersNotification',
  'labelUpdate',
  'conversationOpenStatus',
  'funnerServiceNotification',
  'UpdateChatUserDontDisturb',
  'updateFunnerUserActive',
  'UpdateCompanyPlatforms',
  'ContactBotStatusChange',
  'Logout',
];

export default function useGlobalSocketEvents() {
  const { logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  //* "Receive message"
  const insertUsersAccordingToFilters = React.useCallback(function (
    currentUser: ChatUserModel,
    newUsersList: Array<ChatUserModel>,
    view: ChatViewUsersId,
  ) {
    const doesUserMatchFilters = doesUserMatchCurrentFilters(currentUser, view);

    if (doesUserMatchFilters) {
      if (newUsersList.length === 0) {
        updateSingleChatUser(currentUser.Id, { IsOpen: true });
      }
      newUsersList.unshift(currentUser);
    }

    return newUsersList;
  }, []);
  const receiveMessageUpdateChatUserInView = React.useCallback(
    (view: ChatViewUsersId, newChatUser: ChatUserModel, newMessage: MessageModel) => {
      const storeState = store.getState();
      const areUsersRequested = storeState.chat.areUsersRequested;
      if (!areUsersRequested[`${view}`]) return [];

      const chatUsers = storeState.chat.chatUsers[`${view}`];
      const targetUser = chatUsers.find(
        (user) => user.UserDisplayPhoneNumber === newChatUser.UserDisplayPhoneNumber,
      );

      const isSameMessage =
        targetUser?.LastMessageType === newChatUser?.LastMessageType &&
        (newChatUser?.LastMessageType === 'outcoming'
          ? targetUser?.OutcomingLastMessageId === newChatUser?.OutcomingLastMessageId
          : targetUser?.incomingLastMessageId === newChatUser?.incomingLastMessageId);

      let newUsersList: Array<ChatUserModel> = [];
      if (isSameMessage) {
        newUsersList = chatUsers.map((user) => {
          if (user.UserDisplayPhoneNumber === newChatUser.UserDisplayPhoneNumber) {
            return newChatUser;
          }

          return user;
        });
      } else {
        newUsersList = chatUsers.filter(
          (user) => user.UserDisplayPhoneNumber !== newChatUser.UserDisplayPhoneNumber,
        );

        //* Relate inserting user with active filters
        newUsersList = insertUsersAccordingToFilters(newChatUser, newUsersList, view);
      }

      return newUsersList;
    },
    [insertUsersAccordingToFilters],
  );
  const onSocketReceiveMessage = React.useCallback(
    function (messageObject: ReceiveMessageSocketType) {
      try {
        // טיפול בשני המבנים: ישיר או דרך arguments
        let messageData: any;
        if ('arguments' in messageObject && messageObject.arguments && messageObject.arguments.length > 0) {
          // מבנה עם arguments (מבנה ישן)
          messageData = messageObject.arguments[0];
        } else if ('toUser' in messageObject) {
          // מבנה ישיר (מבנה חדש - ההודעה מגיעה ישירות)
          messageData = messageObject;
        } else {
          return;
        }

        const storeState = store.getState();
        const companyId = storeState.user?.CompanyId;
        const { chatUsers, selectedChatUser } = storeState.chat;

        // מציאת chatUser מהסטור לפי sessionId או toUser
        const allChatUsers = [
          ...chatUsers.myChatUsers,
          ...chatUsers.notMyChatUsers,
          ...chatUsers.unassignedChatUsers,
          ...chatUsers.botsChatUsers,
          ...chatUsers.allChatUsers,
          ...chatUsers.customChatUsers,
        ];

        let chatUser = messageData.sessionId
          ? allChatUsers.find((u) => u.SessionId === messageData.sessionId)
          : null;

        if (!chatUser) {
          chatUser = allChatUsers.find((u) => u.UserDisplayPhoneNumber === messageData.toUser);
        }

        if (!chatUser && selectedChatUser?.UserDisplayPhoneNumber === messageData.toUser) {
          chatUser = selectedChatUser;
        }

        // בדיקת CompanyID
        if (!chatUser || chatUser.CompanyID !== companyId) {
          return;
        }

        // המרת ההודעה למבנה פנימי
        const normalizedType = messageData.direction === 'outgoing' ? 'outcoming' : messageData.direction;

        const newMessage: MessageModel = {
          Id: messageData.id,
          MessageId: messageData.whatsappMessageId,
          messageId: messageData.whatsappMessageId,
          Message: messageData.message,
          Type: normalizedType,
          DateCreate: messageData.date,
          DateCreatedOriginal: messageData.date,
          DateCreateRaw: messageData.date,
          FileType: messageData.type as any,
          File: messageData.fileUrl,
          Status: messageData.status as any,
          reactions: messageData.reaction || null,
          Reply: messageData.replyMessage || null,
          Imported: messageData.imported || null,
          Recovered: messageData.recovered || null,
          IsForwarded: messageData.isForwarded || null,
          ToUser: messageData.toUser,
          CompanyId: companyId,
          GlobalUserId: null,
          StatusHeb: null,
          TempId: null,
          BotName: null,
          Object: null,
        };

        // בדיקה אם צריך להחזיר מוקדם
        if (!newMessage.Id || newMessage.FileType === 'info') {
          return;
        }

        // עדכון chatUser עם המידע החדש
        const newChatUser: ChatUserModel = {
          ...chatUser,
          LastMessage: newMessage.Message || chatUser.LastMessage,
          LastMessageDate: newMessage.DateCreate || chatUser.LastMessageDate,
          LastMessageType: newMessage.Type || chatUser.LastMessageType,
          LastMessageMediaType: newMessage.FileType || chatUser.LastMessageMediaType,
          ...(newMessage.Type === 'incoming'
            ? {
                IncomingLastMessage: newMessage.Message || null,
                IncomingLastMessageDate: newMessage.DateCreate || null,
                incomingLastMessageId: newMessage.Id || null,
              }
            : {}),
          ...(newMessage.Type === 'outcoming'
            ? {
                OutcomingLastMessage: newMessage.Message || null,
                OutcomingLastMessageDate: newMessage.DateCreate || null,
                OutcomingLastMessageId: newMessage.Id || null,
                OutcomingLastMessageStatus: newMessage.Status || null,
              }
            : {}),
        };

        // עדכון ה-views
        const newChatUsers: typeof chatUsers = (
          Object.keys(chatUsers) as Array<keyof typeof chatUsers>
        ).reduce(
          (acc, key) => {
            if (key === 'loading') {
              acc[key] = chatUsers[key];
            } else {
              acc[key] = receiveMessageUpdateChatUserInView(key, newChatUser, newMessage);
            }

            return acc;
          },
          {} as typeof chatUsers,
        );

        dispatch(
          setChatUsers({
            allChatUsers: newChatUsers.allChatUsers,
            myChatUsers: newChatUsers.myChatUsers,
            notMyChatUsers: newChatUsers.notMyChatUsers,
            unassignedChatUsers: newChatUsers.unassignedChatUsers,
            botsChatUsers: newChatUsers.botsChatUsers,
            customChatUsers: newChatUsers.customChatUsers,
          }),
        );

        // עדכון selectedChatUser אם זה המשתמש הנבחר
        if (selectedChatUser?.UserDisplayPhoneNumber === newChatUser.UserDisplayPhoneNumber) {
          dispatch(setSelectedChatUser(newChatUser));
        }

        // עדכון הודעות שמורות
        const thisUserSavedMessages = storeState.chat.chatUsersSavedMessages.find(
          (usr) => usr.id === messageData.toUser,
        );
        if (thisUserSavedMessages) {
          dispatch(saveSingleMessageToUser({ id: messageData.toUser, message: newMessage }));

          const existingMessageIndex = thisUserSavedMessages.messages.findIndex(
            (msg) => msg.Id === newMessage.Id,
          );
          writePerformanceLog('MessagesHealthLog', {
            action:
              existingMessageIndex !== -1
                ? 'ReceiveMessageUpdateInChatUsersMessage'
                : 'ReceiveMessageInsertedToChatUsersMessage',
            date: new Date().toISOString(),
            additionalData: {
              messageId: newMessage.Id || '',
              messageTempId: newMessage.TempId || newMessage.tempId || '',
            },
          });
        }

        // עדכון unread
        processUnreadUser(newChatUser);
      } catch (error: any) {
        console.log('onSocketReceiveMessage error', error);
        // Extract message ID from either structure
        let messageId = '';
        if ('arguments' in messageObject && messageObject.arguments && messageObject.arguments.length > 0) {
          messageId = String(messageObject.arguments[0]?.id || '');
        } else if ('toUser' in messageObject) {
          messageId = String((messageObject as any).id || '');
        }
        writePerformanceLog('MessagesHealthLog', {
          action: 'ReceiveMessageError',
          date: new Date().toISOString(),
          additionalData: {
            messageId,
            error: {
              name: error.name || 'UnknownError',
              message: error.message || 'No message provided',
              stack: error.stack || 'No stack trace available',
            },
          },
        });
      }
    },
    [receiveMessageUpdateChatUserInView],
  );
  //* Update message
  const onSocketUpdateMessage = React.useCallback((messageObject: UpdateMessageSocketType) => {
    try {
      const value = messageObject.entry[0].changes[0].value;
      const statuses = value.statuses?.[0];
      if (!statuses) return;
      const storeState = store.getState();
      const { chatUsersSavedMessages } = storeState.chat;

      const thisUserSavedMessage = chatUsersSavedMessages.find((usr) => usr.id === statuses.recipient_id);
      if (!thisUserSavedMessage) return;

      let existingMessage = thisUserSavedMessage.messages.find(
        (msg) => msg.messageId === statuses.id || msg.MessageId === statuses.id,
      );
      if (!existingMessage) return;

      existingMessage = {
        ...existingMessage,
        Status: getMessageStatus(statuses.status ?? '', existingMessage.Status ?? ''),
      };
      dispatch(saveSingleMessageToUser({ id: statuses.recipient_id, message: existingMessage }));
    } catch (error) {
      console.warn(error);
    }
  }, []);
  //* ReceiveTemplateMessage
  const receiveTemplateMessageUpdateChatUserInView = React.useCallback(
    (view: ChatViewUsersId, messageObject: ReceiveTemplateMessageSocketType) => {
      const storeState = store.getState();
      const areUsersRequested = storeState.chat.areUsersRequested;
      if (!areUsersRequested[`${view}`]) return [];

      const chatUsers = storeState.chat.chatUsers[`${view}`];

      let targetUser = chatUsers.find((user) => user.UserDisplayPhoneNumber === messageObject.phone);
      if (!targetUser) return chatUsers;

      targetUser = {
        ...targetUser,
        LastMessage: messageObject.displayMessage,
        LastMessageDate: messageObject.date,
        //? LastMessageMediaType: 'template',
        LastMessageType: 'outcoming',
        OutcomingLastMessage: messageObject.displayMessage,
        OutcomingLastMessageStatus: messageObject.Status,
      };

      let newUsersList = storeState.chat.chatUsers.allChatUsers.filter(
        (user) => user.UserDisplayPhoneNumber !== messageObject.phone,
      );
      newUsersList = insertUsersAccordingToFilters(targetUser, newUsersList, view);

      return newUsersList;
    },
    [insertUsersAccordingToFilters],
  );
  const onSocketReceiveTemplateMessage = React.useCallback(
    (messageObject: ReceiveTemplateMessageSocketType) => {
      try {
        const storeState = store.getState();
        const { chatUsers } = storeState.chat;

        //* Fill & update views with new chat user
        const newChatUsers: typeof chatUsers = (
          Object.keys(chatUsers) as Array<keyof typeof chatUsers>
        ).reduce(
          (acc, key) => {
            if (key === 'loading') {
              acc[key] = chatUsers[key];
            } else {
              acc[key] = receiveTemplateMessageUpdateChatUserInView(key, messageObject);
            }

            return acc;
          },
          {} as typeof chatUsers,
        );
        dispatch(
          setChatUsers({
            allChatUsers: newChatUsers.allChatUsers,
            myChatUsers: newChatUsers.myChatUsers,
            notMyChatUsers: newChatUsers.notMyChatUsers,
            unassignedChatUsers: newChatUsers.unassignedChatUsers,
            botsChatUsers: newChatUsers.botsChatUsers,
          }),
        );

        //* Update selected chat user if it is his number
        if (storeState.chat.selectedChatUser?.UserDisplayPhoneNumber === messageObject.phone) {
          dispatch(
            setSelectedChatUser({
              ...storeState.chat.selectedChatUser,
              LastMessage: messageObject.displayMessage,
              LastMessageDate: messageObject.date,
              //? LastMessageMediaType: 'template',
              LastMessageType: 'outcoming',
              OutcomingLastMessage: messageObject.displayMessage,
              OutcomingLastMessageStatus: messageObject.Status,
            }),
          );
        }

        //* update saved messages for this user if exists
        const thisUserSavedMessages = storeState.chat.chatUsersSavedMessages.find(
          (usr) => usr.id === messageObject.phone,
        );
        if (!thisUserSavedMessages) return;

        let currentMessage = thisUserSavedMessages.messages.find(
          (msg) =>
            (msg.TempId && msg.TempId === messageObject.tempId) ||
            (msg.tempId && msg.tempId === messageObject.tempId),
        );
        if (!currentMessage) return;
        currentMessage = {
          ...currentMessage,
          MessageId: messageObject.messageId,
          Status: messageObject.Status,
          StatusHeb: messageObject.StatusHeb,
        };

        dispatch(saveSingleMessageToUser({ id: messageObject.phone, message: currentMessage }));
      } catch (error) {
        console.warn('onSocketReceiveTemplateMessage error: ', error);
      }
    },

    [receiveTemplateMessageUpdateChatUserInView],
  );
  //? Unread Notification

  //* Unread Update
  const onSocketUpdateChatUserUnread = React.useCallback(async function (
    messageObject: UpdateChatUserUnreadSocketType,
  ) {
    try {
      console.log('[useGlobalSocketEvents] onSocketUpdateChatUserUnread called:', {
        messageObject,
        source: messageObject.source,
        id: messageObject.id,
        unreadNumber: messageObject.unreadNumber,
      });
      if (messageObject.source === 'setReadAPI') {
        console.log('[useGlobalSocketEvents] Updating chat user unread count:', {
          id: messageObject.id,
          unreadNumber: messageObject.unreadNumber,
        });
        updateSingleChatUser(messageObject.id, { 
          UnreadMessagesNumber: messageObject.unreadNumber,
          UnreadNumber: messageObject.unreadNumber,
        });

        //* update unread users
        const storeState = store.getState();
        const existingUser = storeState.unreadChatUsers.find((usr) => usr.Id === messageObject.id);
        if (existingUser) {
          const updatedUser = {
            ...existingUser,
            UnreadMessagesNumber: messageObject.unreadNumber,
            UnreadNumber: messageObject.unreadNumber,
          };

          console.log('[useGlobalSocketEvents] Processing unread user update:', {
            existingUser,
            updatedUser,
          });
          processUnreadUser(updatedUser);
        } else {
          if (messageObject.unreadNumber !== 0) {
            let user: ChatUserModel | null | undefined = storeState.chat.chatUsers.allChatUsers.find(
              (usr) => usr.Id === messageObject.id,
            );
            if (!user) {
              user = await getChatUser(messageObject.id);
            }

            if (!user) return;
            processUnreadUser(user);
          }
        }
        console.log('[useGlobalSocketEvents] ✅ Unread count updated successfully');
      } else {
        console.log('[useGlobalSocketEvents] ⚠️ Skipping unread update - source is not setReadAPI:', {
          source: messageObject.source,
        });
      }
    } catch (error) {
      console.warn('onSocketUpdateChatUserUnread error: ', error);
    }
  }, []);
  //TODO Extension browser notifications
  //* Notifications
  const onSocketNotification = React.useCallback(
    (messageObject: any) => {
      try {
        const storeState = store.getState();
        const enableCustomTime = storeState.userConfig.enableCustomTime;
        const linkToObjectNum = storeState.company?.LinkToObjectNum;

        if (enableCustomTime) {
          const phone = messageObject.displayPhoneNumber;
          const currentUser = storeState.chat.selectedChatUser;
          if (phone === currentUser?.UserDisplayPhoneNumber) return;

          const targetLink =
            storeState.company?.CompanyType === 'fireberry' &&
            (messageObject.fireberryAccountLink
              ? messageObject.fireberryAccountLink
              : `https://app.powerlink.co.il/app/views/${
                  linkToObjectNum && linkToObjectNum === 'second' ? '2' : '1'
                }?q=${phone}`);

          if (getBrowserNotifStatus() === 'granted') {
            //? handleBrowserNotification(`Funner - ${targetUser.FullName}`, messageObject.notificationText);
            handleBrowserNotification(`Funner`, messageObject.notificationText);
          } else {
            const currentView = getCurrentView();
            enqueueSnackbar(
              <Grid container spacing={0.5}>
                <Grid size={12}>
                  <Box
                    sx={{ color: 'info.contrastText' }}
                    component={Link}
                    href={currentView.path + '/' + phone}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(currentView.path + '/' + phone);
                    }}
                  >
                    <Grid container spacing={1}>
                      {/* //TODO <Grid>{targetUser?.FullName || ''}</Grid> */}
                      <Grid>{phone}</Grid>
                    </Grid>
                  </Box>
                </Grid>
                <Grid size={12}>{messageObject.notificationText}</Grid>
              </Grid>,
              {
                variant: 'info',
                // @ts-ignore
                iconVariant: storeState.company?.CompanyType === 'fireberry' && {
                  info: <NotificationIcon type={'info'} data={{ link: targetLink }} />,
                },
              },
            );
          }
        }
      } catch (error) {
        console.warn('onSocketNotification error: ', error);
      }
    },
    [enqueueSnackbar, navigate],
  );
  const onSocketTaggedUsersNotification = React.useCallback(
    (messageObject: any) => {
      try {
        const storeState = store.getState();
        const user = storeState.user;
        if (user?.Udid !== messageObject.notifyContactUserglobalid) return;

        const enableCustomTime = storeState.userConfig.enableCustomTime;
        if (enableCustomTime) {
          const phone = messageObject.displayPhoneNumber;
          // const targetUser = chatUsersGlobalRef.current.notMyChats.find(
          //   (user) => user.UserDisplayPhoneNumber === phone,
          // );

          if (getBrowserNotifStatus() === 'granted') {
            //TODO handleBrowserNotification(`Funner - ${targetUser.FullName}`, messageObject.notificationText);
            handleBrowserNotification(`Funner`, messageObject.notificationText);
          } else {
            const currentView = getCurrentView();
            enqueueSnackbar(
              <Grid container spacing={0.5}>
                <Grid size={12}>
                  <Box
                    sx={{ color: 'info.contrastText' }}
                    component={Link}
                    href={currentView.path + '/' + phone}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(currentView.path + '/' + phone);
                    }}
                  >
                    <Grid container spacing={1}>
                      {/* //TODO <Grid>{targetUser?.FullName || ''}</Grid> */}
                      <Grid>{messageObject.displayPhoneNumber}</Grid>
                    </Grid>
                  </Box>
                </Grid>
                <Grid size={12}>{messageObject.notificationText}</Grid>
              </Grid>,
              {
                variant: 'info',
              },
            );
          }
        }
      } catch (error) {
        console.warn('onSocketTaggedUsersNotification error: ', error);
      }
    },
    [enqueueSnackbar, navigate],
  );
  const onSocketFunnerServiceNotification = React.useCallback(
    (messageObject: any) => {
      try {
        const { notificationText, type, durationSeconds } = messageObject;
        const scheme = type.split('/');
        enqueueSnackbar(notificationText, {
          autoHideDuration: durationSeconds * 1000,
          variant: 'info',
          style: {
            backgroundColor: scheme[0],
            color: scheme[1],
          },
          // @ts-ignore
          iconVariant: {
            info: (
              <NotificationIcon
                type="info"
                customColors={{
                  bgcolor: scheme[0],
                  color: scheme[1],
                }}
              />
            ),
          },
        });
      } catch (error) {
        console.warn('onSocketFunnerServiceNotification error: ', error);
      }
    },
    [enqueueSnackbar],
  );
  //* Chat user updates
  const onSocketLabelUpdate = React.useCallback(function (messageObject: LabelUpdateSocketType) {
    try {
      updateSingleChatUserByPhone(messageObject.phoneId, { LableName: messageObject.label });

      //* update unread users
      updateUnreadUserByPhone({
        phone: messageObject.phoneId,
        params: {
          LableName: messageObject.label,
        },
      });
    } catch (error) {
      console.log('onSocketLabelUpdate error: ', error);
    }
  }, []);
  const onSocketConversationOpenStatus = React.useCallback(
    (messageObject: ConversationOpenStatusSocketType) => {
      try {
        const { chatUserPhoneId, isOpen } = messageObject;
        updateSingleChatUserByPhone(chatUserPhoneId, { IsOpen: isOpen });

        //* update unread users
        updateUnreadUserByPhone({
          phone: chatUserPhoneId,
          params: {
            IsOpen: isOpen,
          },
        });

        //? const storeState = store.getState();
        // const { chatUsers } = storeState.chat;

        // const { open } = storeState.chat.chatUsersFilters;
        // const doesUserMatchFilters =
        //   open === 'open' ? isOpen === true : open === 'close' ? isOpen === false : true;

        // const newChatUsers: typeof chatUsers = (
        //   Object.keys(chatUsers) as Array<keyof typeof chatUsers>
        // ).reduce(
        //   (acc, key) => {
        //     if (key === 'loading') {
        //       acc[key] = chatUsers[key];
        //     } else {
        //       if (!doesUserMatchFilters) {
        //         acc[key] = chatUsers[key].filter((user) => user.UserDisplayPhoneNumber !== chatUserPhoneId);
        //       }
        //     }

        //     return acc;
        //   },
        //   {} as typeof chatUsers,
        // );
      } catch (error) {
        console.warn('onSocketConversationOpenStatus error: ', error);
      }
    },
    [],
  );
  const onSocketUpdateChatUserDontDisturb = React.useCallback(
    (messageObject: UpdateChatUserDontDisturbSocketType) => {
      try {
        const dontDisturb = messageObject.chatUser.DontDisturb;
        updateSingleChatUser(messageObject.chatUser.Id, { DontDisturb: dontDisturb });

        const storeState = store.getState();
        const existingUser = storeState.unreadChatUsers.find(
          (usr) => usr.UserDisplayPhoneNumber === messageObject.chatUser.UserDisplayPhoneNumber,
        );
        if (existingUser && dontDisturb) {
          removeUnreadUser(messageObject.chatUser.UserDisplayPhoneNumber);
        } else if (!existingUser && !dontDisturb && (messageObject.chatUser.UnreadMessagesNumber || 0) > 0) {
          addUnreadUser(messageObject.chatUser);
        }
      } catch (error) {
        console.warn('onSocketUpdateChatUserDontDisturb error: ', error);
      }
    },
    [],
  );
  const onSocketContactBotStatusChange = React.useCallback((messageObject: any) => {
    try {
      const { UserId, isBot } = messageObject;
      updateSingleChatUserByPhone(UserId, { isBot });

      //* update unread users
      updateUnreadUserByPhone({
        phone: UserId,
        params: {
          isBot,
        },
      });
    } catch (error) {
      console.log('onSocketUpdateFunnerUserActive error: ', error);
    }
  }, []);

  //* Update app users
  const onSocketUpdateFunnerUserActive = React.useCallback(
    (messageObject: any) => {
      try {
        const { funnerUserId, isActive } = messageObject;
        const user = store.getState().user;
        if (funnerUserId === user?.Udid) return logout();

        dispatch(
          updateSingleApplicationUser({
            userId: funnerUserId,
            params: {
              IsActive: isActive,
            },
          }),
        );
      } catch (error) {
        console.warn('onSocketUpdateFunnerUserActive error: ', error);
      }
    },
    [logout],
  );
  //* Company platforms update
  const onSocketCompanyPlatformsChanged = React.useCallback(
    (messageObject: UpdateCompanyPlatformsSocketType) => {
      try {
        const { ChangeNiceTextObject, EditorName, NewObjectJson } = messageObject;

        const parsedPlatforms: CompanyPlatformsModel = JSON.parse(NewObjectJson);
        dispatch(updateCompanyPlatformsData(parsedPlatforms));

        // Don't show notification if current user made the change
        const currentUser = store.getState().user;
        const isCurrentUser = currentUser?.FullName === EditorName || currentUser?.FirstName === EditorName;
        
        if (!isCurrentUser) {
          enqueueSnackbar(ChangeNiceTextObject + ' by ' + EditorName, {
            variant: 'info',
          });
        }
      } catch (error) {
        console.warn('onSocketCompanyPlatformsChanged error: ', error);
      }
    },
    [],
  );

  const onSocketNotif = React.useCallback(
    (type: SocketEventType, message: string | null | any) => {
      // אם ההודעה היא כבר אובייקט, נשתמש בה ישירות, אחרת ננסה לפרסר אותה
      const messageObject = typeof message === 'object' && message !== null ? message : safeParseJSON(message);
      if (!messageObject) return;
      switch (type) {
        case 'ReceiveMessage':
        case 'message:new':
          onSocketReceiveMessage(messageObject);
          break;
        case 'MessageStatusUpdate':
          onSocketUpdateMessage(messageObject);
          break;
        case 'ReceiveTemplateMessage':
          onSocketReceiveTemplateMessage(messageObject);
          break;
        case 'unreadNotification':
          break;
        case 'UnreadUpdate':
          onSocketUpdateChatUserUnread(messageObject);
          break;
        case 'updateChatUserUnread':
          onSocketUpdateChatUserUnread(messageObject);
          break;
        case 'Notification':
          onSocketNotification(messageObject);
          break;
        case 'TaggedUsersNotification':
          onSocketTaggedUsersNotification(messageObject);
          break;
        case 'funnerServiceNotification':
          onSocketFunnerServiceNotification(messageObject);
          break;
        case 'labelUpdate':
          onSocketLabelUpdate(messageObject);
          break;
        case 'conversationOpenStatus':
          onSocketConversationOpenStatus(messageObject);
          break;
        case 'UpdateChatUserDontDisturb':
          onSocketUpdateChatUserDontDisturb(messageObject);
          break;
        case 'ContactBotStatusChange':
          onSocketContactBotStatusChange(messageObject);
          break;
        case 'updateFunnerUserActive':
          onSocketUpdateFunnerUserActive(messageObject);
          break;
        case 'UpdateCompanyPlatforms':
          onSocketCompanyPlatformsChanged(messageObject);
          break;
        case 'Logout':
          logout();
          break;
        default:
          break;
      }

      //TODO if (applicationType === 'extension') {
      //   const sendContent = {
      //     messageObject: messageObject,
      //   };

      //   sendMessageToBackground('socketReceiveMessage', sendContent);
      // }
    },
    [
      logout,
      onSocketCompanyPlatformsChanged,
      onSocketContactBotStatusChange,
      onSocketConversationOpenStatus,
      onSocketFunnerServiceNotification,
      onSocketLabelUpdate,
      onSocketNotification,
      onSocketReceiveMessage,
      onSocketReceiveTemplateMessage,
      onSocketTaggedUsersNotification,
      onSocketUpdateChatUserDontDisturb,
      onSocketUpdateChatUserUnread,
      onSocketUpdateFunnerUserActive,
      onSocketUpdateMessage,
    ],
  );

  useSocketListeners(socketEvents, onSocketNotif);
}
